// SPDX-FileCopyrightText: Copyright 2025 New Vector Ltd.
// SPDX-FileCopyrightText: Copyright 2025, 2026 Element Creations Ltd.
//
// SPDX-License-Identifier: AGPL-3.0-only OR LicenseRef-Element-Commercial

import {
  infiniteQueryOptions,
  type QueryClient,
  queryOptions,
} from "@tanstack/react-query";
import { notFound } from "@tanstack/react-router";

import { authMetadataQuery } from "@/api/auth";
import { formatMxid, getMatrixDomain, wellKnownQuery } from "@/api/matrix";
import { PAGE_SIZE } from "@/constants";
import { HttpStatusError, MasApiError } from "@/errors";
import { accessToken, useAuthStore } from "@/stores/auth";
import { ensureResponseOk, fetch } from "@/utils/fetch";

import * as api from "./api";
import { createClient, type Client } from "./api/client";

const masClient = createClient({
  fetch,
});

const isStaticSynapseAuth = (): boolean =>
  Boolean(useAuthStore.getState().credentials?.static);

interface SynapseThreepid {
  medium: string;
  address: string;
  added_at?: number;
  validated_at?: number;
}

interface SynapseUserRecord {
  name: string;
  admin?: boolean | number;
  deactivated?: boolean | number;
  locked?: boolean;
  is_guest?: boolean | number;
  creation_ts?: number;
  threepids?: SynapseThreepid[];
}

interface SynapseUsersListPayload {
  users?: SynapseUserRecord[];
  next_token?: string | number;
  total?: number;
}

interface SynapseRegistrationTokenRecord {
  token: string;
  uses_allowed: number | null;
  pending: number;
  completed: number;
  expiry_time: number | null;
}

const toIsoTimestamp = (ts?: number | null): string => {
  if (!ts) {
    return new Date().toISOString();
  }
  const ms = ts > 100_000_000_000 ? ts : ts * 1000;
  return new Date(ms).toISOString();
};

const extractLocalpart = (mxidOrUsername: string): string => {
  if (mxidOrUsername.startsWith("@")) {
    const colonIndex = mxidOrUsername.indexOf(":");
    return colonIndex > 1
      ? mxidOrUsername.slice(1, colonIndex)
      : mxidOrUsername.slice(1);
  }
  return mxidOrUsername;
};

const mapSynapseUserToResource = (
  user: SynapseUserRecord,
  cursor?: string | null,
): api.SingleResourceForUser => {
  const username = extractLocalpart(user.name);
  const createdAt = toIsoTimestamp(user.creation_ts);
  const isDeactivated = Boolean(user.deactivated);
  const isLocked = Boolean(user.locked);

  return {
    type: "user",
    id: username,
    attributes: {
      username,
      created_at: createdAt,
      admin: Boolean(user.admin),
      deactivated_at: isDeactivated ? createdAt : null,
      locked_at: isLocked ? createdAt : null,
      legacy_guest: Boolean(user.is_guest),
    },
    links: {
      self: `/_synapse/admin/v2/users/${encodeURIComponent(user.name)}`,
    },
    ...(cursor ? { meta: { page: { cursor } } } : {}),
  };
};

const mapSynapseTokenToResource = (
  record: SynapseRegistrationTokenRecord,
): api.SingleResourceForUserRegistrationToken => {
  const timesUsed = (record.completed ?? 0) + (record.pending ?? 0);
  const isRevoked = record.uses_allowed === 0;
  const expiresAt =
    record.expiry_time !== null && record.expiry_time !== undefined
      ? toIsoTimestamp(record.expiry_time)
      : null;
  const isExpired = expiresAt !== null && Date.parse(expiresAt) <= Date.now();
  const isUsedUp =
    !isRevoked &&
    record.uses_allowed !== null &&
    record.uses_allowed !== undefined &&
    timesUsed >= record.uses_allowed;
  const nowIso = new Date().toISOString();

  return {
    type: "user_registration_token",
    id: record.token,
    attributes: {
      token: record.token,
      valid: !isRevoked && !isExpired && !isUsedUp,
      usage_limit: isRevoked ? null : record.uses_allowed,
      times_used: timesUsed,
      created_at: nowIso,
      last_used_at: timesUsed > 0 ? nowIso : null,
      expires_at: expiresAt,
      revoked_at: isRevoked ? nowIso : null,
    },
    links: {
      self: `/_synapse/admin/v1/registration_tokens/${encodeURIComponent(record.token)}`,
    },
  };
};

const synapseAdminContext = async (
  client: QueryClient,
  serverName: string,
  signal?: AbortSignal,
): Promise<{
  synapseRoot: string;
  headers: HeadersInit;
  signal?: AbortSignal;
}> => {
  const token = await accessToken(client, signal);
  const wellKnown = await client.ensureQueryData(wellKnownQuery(serverName));
  return {
    synapseRoot: wellKnown["m.homeserver"].base_url,
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
    ...(signal && { signal }),
  };
};

const fetchSynapseUser = async (
  client: QueryClient,
  serverName: string,
  userId: string,
  signal?: AbortSignal,
  handleNotFound = false,
): Promise<SynapseUserRecord> => {
  const { synapseRoot, headers } = await synapseAdminContext(
    client,
    serverName,
    signal,
  );
  const mxid = formatMxid(userId, serverName);
  const url = new URL(
    `/_synapse/admin/v2/users/${encodeURIComponent(mxid)}`,
    synapseRoot,
  );
  const response = await fetch(url, { headers, signal });
  if (handleNotFound && response.status === 404) {
    throw notFound();
  }
  ensureResponseOk(response);
  return (await response.json()) as SynapseUserRecord;
};

const updateSynapseUser = async (
  client: QueryClient,
  serverName: string,
  userId: string,
  body: Record<string, unknown>,
  signal?: AbortSignal,
): Promise<api.SingleResponseForUser> => {
  const { synapseRoot, headers } = await synapseAdminContext(
    client,
    serverName,
    signal,
  );
  const mxid = formatMxid(userId, serverName);
  const url = new URL(
    `/_synapse/admin/v2/users/${encodeURIComponent(mxid)}`,
    synapseRoot,
  );
  const response = await fetch(url, {
    method: "PUT",
    headers,
    body: JSON.stringify(body),
    signal,
  });
  ensureResponseOk(response);
  const updated = (await response.json()) as SynapseUserRecord;
  const resource = mapSynapseUserToResource(updated);
  return {
    data: resource,
    links: resource.links,
  };
};

export const isErrorResponse = (t: unknown): t is api.ErrorResponse =>
  typeof t === "object" &&
  t !== null &&
  Object.hasOwn(t, "errors") &&
  Array.isArray((t as api.ErrorResponse).errors);

function ensureNoError<
  R extends {
    data: unknown;
    error: unknown;
    response?: Response;
    request?: Request;
  },
>(
  result: R,
  handleNotFound = false,
): asserts result is R & { data: NonNullable<R["data"]>; error: undefined } {
  if (handleNotFound && result.response?.status === 404) {
    console.warn(
      `MAS replied with a 404 on ${result.request?.method} request to ${result.request?.url}, throwing a 'not found' error`,
    );
    throw notFound({
      data: result.error,
    });
  }
  if (result.error !== undefined) {
    // MAS errors arrive as a decoded body rather than an `Error`: a structured
    // one is turned into prose, anything else — including an error list with
    // nothing in it, which would render as a dangling colon — falls back to the
    // HTTP status so that the error screen never has to render the body itself.
    if (isErrorResponse(result.error) && result.error.errors.length > 0) {
      throw new MasApiError(
        result.error.errors.map((error) => error.title).join(", "),
      );
    }
    if (result.response) {
      throw new HttpStatusError(result.response, { cause: result.error });
    }
    throw result.error;
  }
  if (result.data === undefined) {
    throw new TypeError("Missing data");
  }
}

type WithData<R extends { data?: unknown[] | null | undefined }> = R & {
  data: NonNullable<R["data"]>;
};
function ensureHasData<R extends { data?: unknown[] | null | undefined }>(
  response: R,
): asserts response is WithData<R> {
  if (!Array.isArray(response?.data)) {
    throw new TypeError("Unexpected response from MAS");
  }
}

function ensureHasCount<R extends { meta?: { count?: number | null } | null }>(
  response: R,
): asserts response is R & { meta: { count: number } } {
  if (typeof response.meta?.count !== "number") {
    throw new TypeError("Unexpected response from MAS");
  }
}

interface SingleResource {
  readonly id: string;
  readonly meta?: {
    readonly page?: {
      cursor: string;
    } | null;
  } | null;
}

// Extracts the cursor from an item on a page. This works with both MAS 1.4.0
// and earlier versions
const cursorForSingleResource = (
  resource: SingleResource | null | undefined,
): string | null => resource?.meta?.page?.cursor ?? resource?.id ?? null;

const masBaseOptions = async (
  client: QueryClient,
  serverName: string,
  signal?: AbortSignal,
): Promise<{
  client: Client;
  auth: string;
  baseUrl: string;
  throwOnError: false;
  signal?: AbortSignal;
}> => {
  const token = await accessToken(client, signal);
  const wellKnown = await client.ensureQueryData(wellKnownQuery(serverName));

  const authMetadata = await client.ensureQueryData(
    authMetadataQuery(wellKnown["m.homeserver"].base_url),
  );

  // There is an edge-case where the issuer is not the same as where MAS is deployed.
  // In this case, we rely on the GraphQL endpoint to determine the MAS API
  // root. Ideally MAS would tell us in the metadata the exact base.
  let baseUrl = authMetadata.issuer.replace(/\/$/, "");
  if (
    authMetadata["org.matrix.matrix-authentication-service.graphql_endpoint"]
  ) {
    baseUrl = authMetadata[
      "org.matrix.matrix-authentication-service.graphql_endpoint"
    ].replace(/\/graphql$/, "");
  }

  return {
    client: masClient,
    auth: token,
    baseUrl,
    throwOnError: false,
    ...(signal && { signal }),
  };
};

interface PageParameters {
  before?: string;
  after?: string;
  first?: number;
  last?: number;
}

export interface UserListFilters {
  admin?: boolean;
  guest?: boolean;
  status?: "active" | "locked" | "deactivated";
  search?: string;
  activeOauth2Client?: api.Ulid[];
  hasActiveCompatSession?: boolean;
}

export interface OAuth2ClientListParameters {
  kind?: api.OAuth2ClientKind;
  name?: string;
  uri?: string;
  grantType?: string;
  hasActiveSessions?: boolean;
}

export interface OAuth2SessionListParameters {
  user?: api.Ulid;
  client?: api.Ulid[];
  clientKind?: api.OAuth2ClientKind;
  userSession?: api.Ulid;
  scope?: string[];
  status?: api.OAuth2SessionStatus;
  createdAfter?: string;
  createdBefore?: string;
  lastActiveAfter?: string;
  lastActiveBefore?: string;
}

export interface CompatSessionListParameters {
  user?: api.Ulid;
  userSession?: api.Ulid;
  status?: api.CompatSessionStatus;
  createdAfter?: string;
  createdBefore?: string;
  lastActiveAfter?: string;
  lastActiveBefore?: string;
}

export interface TokenListParameters extends PageParameters {
  used?: boolean;
  revoked?: boolean;
  expired?: boolean;
  valid?: boolean;
}

export interface CreateTokenParameters {
  token?: string; // Custom token string (optional)
  usage_limit?: number;
  expires_at?: string; // ISO date string
}

export interface EditTokenParameters {
  usage_limit?: number | null;
  expires_at?: string | null; // ISO date string
}

export interface PersonalSessionListParameters extends PageParameters {
  status?: api.PersonalSessionStatus;
  actor_user?: api.Ulid;
  expires?: boolean;
  scope?: string[];
}

export interface CreatePersonalSessionParameters {
  actor_user_id: api.Ulid;
  human_name: string;
  scope: string;
  expires_in?: number | null;
}

interface RegeneratePersonalSessionParameters {
  expires_in?: number | null;
}

// FIXME: pagination direction is temporary until MAS gets proper ordering in the API
type PaginationDirection = "forward" | "backward";

const defaultSiteConfig = (serverName: string): api.SiteConfig => ({
  account_deactivation_allowed: true,
  account_recovery_allowed: true,
  captcha_enabled: false,
  displayname_change_allowed: true,
  email_change_allowed: true,
  minimum_password_complexity: 3,
  password_change_allowed: true,
  password_login_enabled: true,
  password_registration_enabled: true,
  password_registration_email_required: false,
  registration_token_required: true,
  server_name: getMatrixDomain(serverName),
});

const buildSynapseUsersUrl = (
  synapseRoot: string,
  parameters: UserListFilters,
  direction: PaginationDirection,
  limit: number,
  from?: string | null,
): URL => {
  const url = new URL("/_synapse/admin/v2/users", synapseRoot);
  url.searchParams.set("limit", String(limit));
  url.searchParams.set("dir", direction === "backward" ? "b" : "f");
  if (from) url.searchParams.set("from", from);
  if (parameters.search) url.searchParams.set("name", parameters.search);
  if (parameters.admin !== undefined)
    url.searchParams.set("admins", String(parameters.admin));
  if (parameters.guest !== undefined)
    url.searchParams.set("guests", String(parameters.guest));
  if (parameters.status === "deactivated") {
    url.searchParams.set("deactivated", "true");
  }
  return url;
};

const fetchFilteredSynapseTokens = async (
  client: QueryClient,
  serverName: string,
  parameters: TokenListParameters,
  signal?: AbortSignal,
): Promise<api.SingleResourceForUserRegistrationToken[]> => {
  const { synapseRoot, headers } = await synapseAdminContext(
    client,
    serverName,
    signal,
  );
  const url = new URL("/_synapse/admin/v1/registration_tokens", synapseRoot);
  const response = await fetch(url, { headers, signal });
  ensureResponseOk(response);
  const body = (await response.json()) as {
    registration_tokens?: SynapseRegistrationTokenRecord[];
  };
  const resources = (body.registration_tokens ?? []).map((record) =>
    mapSynapseTokenToResource(record),
  );
  return resources.filter((item) => {
    const attr = item.attributes;
    if (parameters.valid !== undefined && attr.valid !== parameters.valid) {
      return false;
    }
    if (
      parameters.used !== undefined &&
      attr.times_used > 0 !== parameters.used
    ) {
      return false;
    }
    if (
      parameters.revoked !== undefined &&
      Boolean(attr.revoked_at) !== parameters.revoked
    ) {
      return false;
    }
    if (parameters.expired !== undefined) {
      const isExpired =
        Boolean(attr.expires_at) &&
        Date.parse(attr.expires_at ?? "") <= Date.now();
      if (isExpired !== parameters.expired) {
        return false;
      }
    }
    return true;
  });
};

export const siteConfigQuery = (serverName: string) =>
  queryOptions({
    queryKey: ["mas", "site-config", serverName],
    queryFn: async ({ client, signal }): Promise<api.SiteConfig> => {
      if (isStaticSynapseAuth()) {
        return defaultSiteConfig(serverName);
      }
      try {
        const result = await api.siteConfig({
          ...(await masBaseOptions(client, serverName, signal)),
        });
        ensureNoError(result);
        return result.data;
      } catch (error) {
        console.warn(
          "Site-config query failed, this is likely because of talking to an older version of MAS, ignoring",
          error,
        );
        return defaultSiteConfig(serverName);
      }
    },
  });

export const versionQuery = (serverName: string) =>
  queryOptions({
    queryKey: ["mas", "version", serverName],
    queryFn: async ({ client, signal }): Promise<api.Version> => {
      if (isStaticSynapseAuth()) {
        return {
          version: "v0.0.0-synapse",
        };
      }
      try {
        const result = await api.version({
          ...(await masBaseOptions(client, serverName, signal)),
        });
        ensureNoError(result);
        return result.data;
      } catch (error) {
        console.warn(
          "Version query failed, this is likely because of talking to an older version of MAS, ignoring",
          error,
        );

        // Fallback to an unknown version, valid semver
        return {
          version: "v0.0.0-unknown",
        };
      }
    },
  });

export const usersInfiniteQuery = (
  serverName: string,
  parameters: UserListFilters = {},
  direction: PaginationDirection = "forward",
) =>
  infiniteQueryOptions({
    queryKey: ["mas", "users", serverName, parameters, direction],
    queryFn: async ({
      client,
      signal,
      pageParam,
    }): Promise<WithData<api.PaginatedResponseForUser>> => {
      if (isStaticSynapseAuth()) {
        const { synapseRoot, headers } = await synapseAdminContext(
          client,
          serverName,
          signal,
        );
        const url = buildSynapseUsersUrl(
          synapseRoot,
          parameters,
          direction,
          PAGE_SIZE,
          pageParam,
        );
        const response = await fetch(url, { headers, signal });
        ensureResponseOk(response);
        const payload = (await response.json()) as SynapseUsersListPayload;
        const nextCursor =
          payload.next_token !== undefined && payload.next_token !== null
            ? String(payload.next_token)
            : null;
        let users = payload.users ?? [];
        if (parameters.status === "locked") {
          users = users.filter((u) => Boolean(u.locked));
        } else if (parameters.status === "active") {
          users = users.filter((u) => !u.locked && !u.deactivated);
        }
        const data = users.map((u) => mapSynapseUserToResource(u, nextCursor));
        return {
          data,
          meta: { count: payload.total ?? data.length },
          links: {
            self: url.pathname + url.search,
            ...(direction === "forward" && nextCursor
              ? { next: nextCursor }
              : {}),
            ...(direction === "backward" && nextCursor
              ? { prev: nextCursor }
              : {}),
          },
        };
      }

      const query: api.ListUsersData["query"] = {
        count: "false",
      };

      if (direction === "forward") {
        query["page[first]"] = PAGE_SIZE;
        if (pageParam) query["page[after]"] = pageParam;
      } else {
        query["page[last]"] = PAGE_SIZE;
        if (pageParam) query["page[before]"] = pageParam;
      }

      if (parameters.admin !== undefined)
        query["filter[admin]"] = parameters.admin;
      if (parameters.guest !== undefined)
        query["filter[legacy-guest]"] = parameters.guest;
      if (parameters.status) query["filter[status]"] = parameters.status;
      if (parameters.search) query["filter[search]"] = parameters.search;
      if (
        parameters.activeOauth2Client &&
        parameters.activeOauth2Client.length > 0
      )
        query["filter[active-oauth2-client]"] = parameters.activeOauth2Client;
      if (parameters.hasActiveCompatSession !== undefined)
        query["filter[has-active-compat-session]"] =
          parameters.hasActiveCompatSession;

      const result = await api.listUsers({
        ...(await masBaseOptions(client, serverName, signal)),
        query,
      });
      ensureNoError(result);
      ensureHasData(result.data);
      return result.data;
    },
    initialPageParam: null as string | null,
    getNextPageParam: (lastPage): string | null =>
      (direction === "forward"
        ? lastPage.links.next && cursorForSingleResource(lastPage.data?.at(-1))
        : lastPage.links.prev &&
          cursorForSingleResource(lastPage.data?.at(0))) ?? null,
  });

export const userQuery = (serverName: string, userId: string) =>
  queryOptions({
    queryKey: ["mas", "user", serverName, userId],
    queryFn: async ({ client, signal }): Promise<api.SingleResponseForUser> => {
      if (isStaticSynapseAuth()) {
        const user = await fetchSynapseUser(
          client,
          serverName,
          userId,
          signal,
          true,
        );
        const resource = mapSynapseUserToResource(user);
        return {
          data: resource,
          links: resource.links,
        };
      }
      const result = await api.getUser({
        ...(await masBaseOptions(client, serverName, signal)),
        path: { id: userId },
      });
      ensureNoError(result, true);
      return result.data;
    },
  });

export const usersCountQuery = (
  serverName: string,
  parameters: UserListFilters = {},
) =>
  queryOptions({
    queryKey: ["mas", "users", serverName, parameters, "count"],
    queryFn: async ({ client, signal }): Promise<number> => {
      if (isStaticSynapseAuth()) {
        const { synapseRoot, headers } = await synapseAdminContext(
          client,
          serverName,
          signal,
        );
        const needsClientFilter = parameters.status === "locked";
        const url = buildSynapseUsersUrl(
          synapseRoot,
          parameters,
          "forward",
          needsClientFilter ? 1000 : 1,
        );
        const response = await fetch(url, { headers, signal });
        ensureResponseOk(response);
        const payload = (await response.json()) as SynapseUsersListPayload;
        if (needsClientFilter) {
          return (payload.users ?? []).filter((u) => Boolean(u.locked)).length;
        }
        return payload.total ?? (payload.users ?? []).length;
      }

      const query: api.ListUsersData["query"] = {
        count: "only",
      };

      if (parameters.admin !== undefined)
        query["filter[admin]"] = parameters.admin;
      if (parameters.guest !== undefined)
        query["filter[legacy-guest]"] = parameters.guest;
      if (parameters.status) query["filter[status]"] = parameters.status;
      if (parameters.search) query["filter[search]"] = parameters.search;
      if (
        parameters.activeOauth2Client &&
        parameters.activeOauth2Client.length > 0
      )
        query["filter[active-oauth2-client]"] = parameters.activeOauth2Client;
      if (parameters.hasActiveCompatSession !== undefined)
        query["filter[has-active-compat-session]"] =
          parameters.hasActiveCompatSession;

      const result = await api.listUsers({
        ...(await masBaseOptions(client, serverName, signal)),
        query,
      });
      ensureNoError(result);
      ensureHasCount(result.data);

      return result.data.meta.count;
    },
  });

export const createUser = async (
  queryClient: QueryClient,
  serverName: string,
  username: string,
  signal?: AbortSignal,
): Promise<api.SingleResponseForUser> => {
  if (isStaticSynapseAuth()) {
    return updateSynapseUser(
      queryClient,
      serverName,
      username,
      {
        password: crypto.randomUUID(),
        deactivated: false,
      },
      signal,
    );
  }
  const result = await api.createUser({
    ...(await masBaseOptions(queryClient, serverName, signal)),
    body: {
      username,
    },
  });
  ensureNoError(result);
  return result.data;
};

export const lockUser = async (
  queryClient: QueryClient,
  serverName: string,
  userId: api.Ulid,
  signal?: AbortSignal,
): Promise<api.SingleResponseForUser> => {
  if (isStaticSynapseAuth()) {
    return updateSynapseUser(
      queryClient,
      serverName,
      userId,
      { locked: true },
      signal,
    );
  }
  const result = await api.lockUser({
    ...(await masBaseOptions(queryClient, serverName, signal)),
    path: { id: userId },
  });
  ensureNoError(result);
  return result.data;
};

export const deactivateUser = async (
  queryClient: QueryClient,
  serverName: string,
  userId: api.Ulid,
  signal?: AbortSignal,
): Promise<api.SingleResponseForUser> => {
  if (isStaticSynapseAuth()) {
    return updateSynapseUser(
      queryClient,
      serverName,
      userId,
      { deactivated: true },
      signal,
    );
  }
  const result = await api.deactivateUser({
    ...(await masBaseOptions(queryClient, serverName, signal)),
    path: { id: userId },
  });
  ensureNoError(result);
  return result.data;
};

export const reactivateUser = async (
  queryClient: QueryClient,
  serverName: string,
  userId: api.Ulid,
  signal?: AbortSignal,
): Promise<api.SingleResponseForUser> => {
  if (isStaticSynapseAuth()) {
    try {
      return await updateSynapseUser(
        queryClient,
        serverName,
        userId,
        { deactivated: false },
        signal,
      );
    } catch {
      return updateSynapseUser(
        queryClient,
        serverName,
        userId,
        { deactivated: false, password: crypto.randomUUID() },
        signal,
      );
    }
  }
  const result = await api.reactivateUser({
    ...(await masBaseOptions(queryClient, serverName, signal)),
    path: { id: userId },
  });
  ensureNoError(result);
  return result.data;
};

export const setUserPassword = async (
  queryClient: QueryClient,
  serverName: string,
  userId: api.Ulid,
  password: string,
  skipPasswordCheck = false,
  signal?: AbortSignal,
): Promise<void> => {
  if (isStaticSynapseAuth()) {
    await updateSynapseUser(
      queryClient,
      serverName,
      userId,
      { password, logout_devices: false },
      signal,
    );
    return;
  }
  const result = await api.setUserPassword({
    ...(await masBaseOptions(queryClient, serverName, signal)),
    path: { id: userId },
    body: {
      password,
      skip_password_check: skipPasswordCheck,
    },
  });
  ensureNoError(result);
};

export const setUserCanRequestAdmin = async (
  queryClient: QueryClient,
  serverName: string,
  userId: api.Ulid,
  canRequestAdmin: boolean,
  signal?: AbortSignal,
): Promise<api.SingleResponseForUser> => {
  if (isStaticSynapseAuth()) {
    return updateSynapseUser(
      queryClient,
      serverName,
      userId,
      { admin: canRequestAdmin },
      signal,
    );
  }
  const result = await api.userSetAdmin({
    ...(await masBaseOptions(queryClient, serverName, signal)),
    path: { id: userId },
    body: {
      admin: canRequestAdmin,
    },
  });
  ensureNoError(result);
  return result.data;
};

export const userEmailsQuery = (serverName: string, userId: string) =>
  queryOptions({
    queryKey: ["mas", "user-emails", serverName, userId],
    queryFn: async ({ client, signal }) => {
      if (isStaticSynapseAuth()) {
        const user = await fetchSynapseUser(client, serverName, userId, signal);
        const username = extractLocalpart(user.name);
        const emails = (user.threepids ?? []).filter(
          (tp) => tp.medium === "email",
        );
        const data: api.SingleResourceForUserEmail[] = emails.map((tp) => {
          const id = `${username}:${tp.address}`;
          return {
            type: "user_email",
            id,
            attributes: {
              created_at: toIsoTimestamp(tp.added_at ?? user.creation_ts),
              user_id: username,
              email: tp.address,
            },
            links: {
              self: `/_synapse/admin/v2/users/${encodeURIComponent(user.name)}`,
            },
          };
        });
        return {
          data,
          links: {
            self: `/_synapse/admin/v2/users/${encodeURIComponent(user.name)}`,
          },
        };
      }
      const result = await api.listUserEmails({
        ...(await masBaseOptions(client, serverName, signal)),
        query: {
          "filter[user]": userId,
          "page[first]": 10,
          count: "false",
        },
      });
      ensureNoError(result);
      ensureHasData(result.data);
      return result.data;
    },
  });

export const deleteUserEmail = async (
  queryClient: QueryClient,
  serverName: string,
  emailId: api.Ulid,
  signal?: AbortSignal,
): Promise<void> => {
  if (isStaticSynapseAuth()) {
    const separator = emailId.indexOf(":");
    const userId = separator > 0 ? emailId.slice(0, separator) : emailId;
    const emailAddress = separator > 0 ? emailId.slice(separator + 1) : "";
    const user = await fetchSynapseUser(
      queryClient,
      serverName,
      userId,
      signal,
    );
    const remaining = (user.threepids ?? [])
      .filter((tp) => !(tp.medium === "email" && tp.address === emailAddress))
      .map((tp) => ({ medium: tp.medium, address: tp.address }));
    await updateSynapseUser(
      queryClient,
      serverName,
      userId,
      { threepids: remaining },
      signal,
    );
    return;
  }
  const result = await api.deleteUserEmail({
    ...(await masBaseOptions(queryClient, serverName, signal)),
    path: { id: emailId },
  });
  ensureNoError(result);
};

export const addUserEmail = async (
  queryClient: QueryClient,
  serverName: string,
  userId: api.Ulid,
  email: string,
  signal?: AbortSignal,
): Promise<api.SingleResponseForUserEmail> => {
  if (isStaticSynapseAuth()) {
    const user = await fetchSynapseUser(
      queryClient,
      serverName,
      userId,
      signal,
    );
    const existing = (user.threepids ?? []).map((tp) => ({
      medium: tp.medium,
      address: tp.address,
    }));
    if (!existing.some((tp) => tp.medium === "email" && tp.address === email)) {
      existing.push({ medium: "email", address: email });
    }
    await updateSynapseUser(
      queryClient,
      serverName,
      userId,
      { threepids: existing },
      signal,
    );
    const username = extractLocalpart(user.name);
    const id = `${username}:${email}`;
    const resource: api.SingleResourceForUserEmail = {
      type: "user_email",
      id,
      attributes: {
        created_at: new Date().toISOString(),
        user_id: username,
        email,
      },
      links: {
        self: `/_synapse/admin/v2/users/${encodeURIComponent(user.name)}`,
      },
    };
    return {
      data: resource,
      links: resource.links,
    };
  }
  const result = await api.addUserEmail({
    ...(await masBaseOptions(queryClient, serverName, signal)),
    body: {
      email,
      user_id: userId,
    },
  });
  ensureNoError(result);
  return result.data;
};

export const userUpstreamLinksQuery = (serverName: string, userId: string) =>
  queryOptions({
    queryKey: ["mas", "user-upstream-links", serverName, userId],
    queryFn: async ({ client, signal }) => {
      if (isStaticSynapseAuth()) {
        return {
          data: [] as api.SingleResourceForUpstreamOAuthLink[],
          links: { self: "" },
        };
      }
      const result = await api.listUpstreamOAuthLinks({
        ...(await masBaseOptions(client, serverName, signal)),
        query: {
          "filter[user]": userId,
          "page[first]": 10,
          count: "false",
        },
      });
      ensureNoError(result);
      ensureHasData(result.data);
      return result.data;
    },
  });

export const upstreamProvidersQuery = (serverName: string) =>
  queryOptions({
    queryKey: ["mas", "upstream-providers", serverName],
    queryFn: async ({
      client,
      signal,
    }): Promise<api.SingleResourceForUpstreamOAuthProvider[]> => {
      if (isStaticSynapseAuth()) {
        return [];
      }
      const result = await api.listUpstreamOAuthProviders({
        ...(await masBaseOptions(client, serverName, signal)),
        query: {
          // Let's assume we're not going to have more than 1000 providers
          "page[first]": 1000,
          count: "false",
        },
      });
      ensureNoError(result);
      ensureHasData(result.data);
      return result.data.data;
    },
  });

export const deleteUpstreamOAuthLink = async (
  queryClient: QueryClient,
  serverName: string,
  linkId: api.Ulid,
  signal?: AbortSignal,
): Promise<void> => {
  const result = await api.deleteUpstreamOAuthLink({
    ...(await masBaseOptions(queryClient, serverName, signal)),
    path: { id: linkId },
  });
  ensureNoError(result);
};

export const addUpstreamOAuthLink = async (
  queryClient: QueryClient,
  serverName: string,
  userId: api.Ulid,
  providerId: api.Ulid,
  subject: string,
  signal?: AbortSignal,
): Promise<api.SingleResponseForUpstreamOAuthLink> => {
  const result = await api.addUpstreamOAuthLink({
    ...(await masBaseOptions(queryClient, serverName, signal)),
    body: {
      user_id: userId,
      provider_id: providerId,
      subject,
    },
  });
  ensureNoError(result);
  return result.data;
};

export const unlockUser = async (
  queryClient: QueryClient,
  serverName: string,
  userId: api.Ulid,
  signal?: AbortSignal,
): Promise<api.SingleResponseForUser> => {
  if (isStaticSynapseAuth()) {
    return updateSynapseUser(
      queryClient,
      serverName,
      userId,
      { locked: false },
      signal,
    );
  }
  const result = await api.unlockUser({
    ...(await masBaseOptions(queryClient, serverName, signal)),
    path: { id: userId },
  });
  ensureNoError(result);
  return result.data;
};

export const registrationTokensInfiniteQuery = (
  serverName: string,
  parameters: TokenListParameters = {},
) =>
  infiniteQueryOptions({
    queryKey: ["mas", "registration-tokens", serverName, parameters],
    queryFn: async ({
      client,
      signal,
      pageParam,
    }): Promise<WithData<api.PaginatedResponseForUserRegistrationToken>> => {
      if (isStaticSynapseAuth()) {
        const data = await fetchFilteredSynapseTokens(
          client,
          serverName,
          parameters,
          signal,
        );
        return {
          data,
          meta: { count: data.length },
          links: {
            self: "/_synapse/admin/v1/registration_tokens",
          },
        };
      }
      const query: api.ListUserRegistrationTokensData["query"] = {
        "page[first]": PAGE_SIZE,
        count: "false",
      };

      if (pageParam) query["page[after]"] = pageParam;

      if (parameters.used !== undefined)
        query["filter[used]"] = parameters.used;
      if (parameters.revoked !== undefined)
        query["filter[revoked]"] = parameters.revoked;
      if (parameters.expired !== undefined)
        query["filter[expired]"] = parameters.expired;
      if (parameters.valid !== undefined)
        query["filter[valid]"] = parameters.valid;

      const result = await api.listUserRegistrationTokens({
        ...(await masBaseOptions(client, serverName, signal)),
        query,
      });
      ensureNoError(result);
      ensureHasData(result.data);
      return result.data;
    },
    initialPageParam: null as api.Ulid | null,
    getNextPageParam: (lastPage): api.Ulid | null =>
      (lastPage.links.next && cursorForSingleResource(lastPage.data?.at(-1))) ??
      null,
  });

export const registrationTokensCountQuery = (
  serverName: string,
  parameters: TokenListParameters = {},
) =>
  queryOptions({
    queryKey: ["mas", "registration-tokens", serverName, parameters, "count"],
    queryFn: async ({ client, signal }): Promise<number> => {
      if (isStaticSynapseAuth()) {
        const data = await fetchFilteredSynapseTokens(
          client,
          serverName,
          parameters,
          signal,
        );
        return data.length;
      }
      const query: api.ListUserRegistrationTokensData["query"] = {
        count: "only",
      };

      if (parameters.used !== undefined)
        query["filter[used]"] = parameters.used;
      if (parameters.revoked !== undefined)
        query["filter[revoked]"] = parameters.revoked;
      if (parameters.expired !== undefined)
        query["filter[expired]"] = parameters.expired;
      if (parameters.valid !== undefined)
        query["filter[valid]"] = parameters.valid;
      const result = await api.listUserRegistrationTokens({
        ...(await masBaseOptions(client, serverName, signal)),
        query,
      });
      ensureNoError(result);
      ensureHasCount(result.data);
      return result.data.meta.count;
    },
  });

export const registrationTokenQuery = (serverName: string, tokenId: string) =>
  queryOptions({
    queryKey: ["mas", "registration-token", serverName, tokenId],
    queryFn: async ({
      client,
      signal,
    }): Promise<api.SingleResponseForUserRegistrationToken> => {
      if (isStaticSynapseAuth()) {
        const { synapseRoot, headers } = await synapseAdminContext(
          client,
          serverName,
          signal,
        );
        const url = new URL(
          `/_synapse/admin/v1/registration_tokens/${encodeURIComponent(tokenId)}`,
          synapseRoot,
        );
        const response = await fetch(url, { headers, signal });
        if (response.status === 404) {
          throw notFound();
        }
        ensureResponseOk(response);
        const record =
          (await response.json()) as SynapseRegistrationTokenRecord;
        const resource = mapSynapseTokenToResource(record);
        return {
          data: resource,
          links: resource.links,
        };
      }
      const result = await api.getUserRegistrationToken({
        ...(await masBaseOptions(client, serverName, signal)),
        path: { id: tokenId },
      });
      ensureNoError(result, true);
      return result.data;
    },
  });

export const createRegistrationToken = async (
  queryClient: QueryClient,
  serverName: string,
  parameters: CreateTokenParameters = {},
  signal?: AbortSignal,
): Promise<api.SingleResponseForUserRegistrationToken> => {
  if (isStaticSynapseAuth()) {
    const { synapseRoot, headers } = await synapseAdminContext(
      queryClient,
      serverName,
      signal,
    );
    const url = new URL(
      "/_synapse/admin/v1/registration_tokens/new",
      synapseRoot,
    );
    const synapseBody: {
      token?: string;
      uses_allowed?: number;
      expiry_time?: number;
    } = {};
    if (parameters.token) synapseBody.token = parameters.token;
    if (parameters.usage_limit !== undefined)
      synapseBody.uses_allowed = parameters.usage_limit;
    if (parameters.expires_at)
      synapseBody.expiry_time = Date.parse(parameters.expires_at);
    const response = await fetch(url, {
      method: "POST",
      headers,
      body: JSON.stringify(synapseBody),
      signal,
    });
    ensureResponseOk(response);
    const record = (await response.json()) as SynapseRegistrationTokenRecord;
    const resource = mapSynapseTokenToResource(record);
    return {
      data: resource,
      links: resource.links,
    };
  }
  const body: api.AddUserRegistrationTokenData["body"] = {};

  if (parameters.expires_at) body.expires_at = parameters.expires_at;
  if (parameters.usage_limit !== undefined)
    body.usage_limit = parameters.usage_limit;
  if (parameters.token) body.token = parameters.token;

  const result = await api.addUserRegistrationToken({
    ...(await masBaseOptions(queryClient, serverName, signal)),
    body,
  });
  ensureNoError(result);
  return result.data;
};

export const revokeRegistrationToken = async (
  queryClient: QueryClient,
  serverName: string,
  tokenId: api.Ulid,
  signal?: AbortSignal,
): Promise<api.SingleResponseForUserRegistrationToken> => {
  if (isStaticSynapseAuth()) {
    const { synapseRoot, headers } = await synapseAdminContext(
      queryClient,
      serverName,
      signal,
    );
    const url = new URL(
      `/_synapse/admin/v1/registration_tokens/${encodeURIComponent(tokenId)}`,
      synapseRoot,
    );
    const response = await fetch(url, {
      method: "PUT",
      headers,
      body: JSON.stringify({ uses_allowed: 0 }),
      signal,
    });
    ensureResponseOk(response);
    const record = (await response.json()) as SynapseRegistrationTokenRecord;
    const resource = mapSynapseTokenToResource(record);
    return {
      data: resource,
      links: resource.links,
    };
  }
  const result = await api.revokeUserRegistrationToken({
    ...(await masBaseOptions(queryClient, serverName, signal)),
    path: { id: tokenId },
  });
  ensureNoError(result);
  return result.data;
};

export const unrevokeRegistrationToken = async (
  queryClient: QueryClient,
  serverName: string,
  tokenId: api.Ulid,
  signal?: AbortSignal,
): Promise<api.SingleResponseForUserRegistrationToken> => {
  if (isStaticSynapseAuth()) {
    const { synapseRoot, headers } = await synapseAdminContext(
      queryClient,
      serverName,
      signal,
    );
    const url = new URL(
      `/_synapse/admin/v1/registration_tokens/${encodeURIComponent(tokenId)}`,
      synapseRoot,
    );
    const response = await fetch(url, {
      method: "PUT",
      headers,
      body: JSON.stringify({ uses_allowed: null }),
      signal,
    });
    ensureResponseOk(response);
    const record = (await response.json()) as SynapseRegistrationTokenRecord;
    const resource = mapSynapseTokenToResource(record);
    return {
      data: resource,
      links: resource.links,
    };
  }
  const result = await api.unrevokeUserRegistrationToken({
    ...(await masBaseOptions(queryClient, serverName, signal)),
    path: { id: tokenId },
  });
  ensureNoError(result);
  return result.data;
};

export const personalSessionsInfiniteQuery = (
  serverName: string,
  parameters: PersonalSessionListParameters = {},
) =>
  infiniteQueryOptions({
    queryKey: ["mas", "personal-sessions", serverName, parameters],
    queryFn: async ({
      client,
      signal,
      pageParam,
    }): Promise<WithData<api.PaginatedResponseForPersonalSession>> => {
      const query: api.ListPersonalSessionsData["query"] = {
        "page[first]": PAGE_SIZE,
        count: "false",
      };

      if (pageParam) query["page[after]"] = pageParam;

      if (parameters.status !== undefined)
        query["filter[status]"] = parameters.status;
      if (parameters.actor_user !== undefined)
        query["filter[actor_user]"] = parameters.actor_user;
      if (parameters.expires !== undefined)
        query["filter[expires]"] = parameters.expires;
      if (parameters.scope !== undefined)
        query["filter[scope]"] = parameters.scope;

      const result = await api.listPersonalSessions({
        ...(await masBaseOptions(client, serverName, signal)),
        query,
      });
      ensureNoError(result);
      ensureHasData(result.data);
      return result.data;
    },
    initialPageParam: null as api.Ulid | null,
    getNextPageParam: (lastPage): api.Ulid | null =>
      (lastPage.links.next && cursorForSingleResource(lastPage.data?.at(-1))) ??
      null,
  });

export const personalSessionsCountQuery = (
  serverName: string,
  parameters: PersonalSessionListParameters = {},
) =>
  queryOptions({
    queryKey: ["mas", "personal-sessions", serverName, parameters, "count"],
    queryFn: async ({ client, signal }): Promise<number> => {
      const query: api.ListPersonalSessionsData["query"] = {
        count: "only",
      };

      if (parameters.status !== undefined)
        query["filter[status]"] = parameters.status;
      if (parameters.actor_user !== undefined)
        query["filter[actor_user]"] = parameters.actor_user;
      if (parameters.expires !== undefined)
        query["filter[expires]"] = parameters.expires;
      if (parameters.scope !== undefined)
        query["filter[scope]"] = parameters.scope;

      const result = await api.listPersonalSessions({
        ...(await masBaseOptions(client, serverName, signal)),
        query,
      });
      ensureNoError(result);
      ensureHasCount(result.data);
      return result.data.meta.count;
    },
  });

export const personalSessionQuery = (serverName: string, sessionId: string) =>
  queryOptions({
    queryKey: ["mas", "personal-session", serverName, sessionId],
    queryFn: async ({
      client,
      signal,
    }): Promise<api.SingleResponseForPersonalSession> => {
      const result = await api.getPersonalSession({
        ...(await masBaseOptions(client, serverName, signal)),
        path: { id: sessionId },
      });
      ensureNoError(result, true);
      return result.data;
    },
  });

export const createPersonalSession = async (
  queryClient: QueryClient,
  serverName: string,
  parameters: CreatePersonalSessionParameters,
  signal?: AbortSignal,
): Promise<api.SingleResponseForPersonalSession> => {
  const body: api.CreatePersonalSessionData["body"] = {
    actor_user_id: parameters.actor_user_id,
    human_name: parameters.human_name,
    scope: parameters.scope,
  };

  if (parameters.expires_in !== undefined)
    body.expires_in = parameters.expires_in;

  const result = await api.createPersonalSession({
    ...(await masBaseOptions(queryClient, serverName, signal)),
    body,
  });
  ensureNoError(result);
  return result.data;
};

export const revokePersonalSession = async (
  queryClient: QueryClient,
  serverName: string,
  sessionId: api.Ulid,
  signal?: AbortSignal,
): Promise<api.SingleResponseForPersonalSession> => {
  const result = await api.revokePersonalSession({
    ...(await masBaseOptions(queryClient, serverName, signal)),
    path: { id: sessionId },
  });
  ensureNoError(result);
  return result.data;
};

export const regeneratePersonalSession = async (
  queryClient: QueryClient,
  serverName: string,
  sessionId: api.Ulid,
  parameters: RegeneratePersonalSessionParameters = {},
  signal?: AbortSignal,
): Promise<api.SingleResponseForPersonalSession> => {
  const body: api.RegeneratePersonalSessionData["body"] = {};

  if (parameters.expires_in !== undefined)
    body.expires_in = parameters.expires_in;

  const result = await api.regeneratePersonalSession({
    ...(await masBaseOptions(queryClient, serverName, signal)),
    path: { id: sessionId },
    body,
  });
  ensureNoError(result);
  return result.data;
};

export const editRegistrationToken = async (
  queryClient: QueryClient,
  serverName: string,
  tokenId: api.Ulid,
  parameters: EditTokenParameters,
  signal?: AbortSignal,
): Promise<api.SingleResponseForUserRegistrationToken> => {
  if (isStaticSynapseAuth()) {
    const { synapseRoot, headers } = await synapseAdminContext(
      queryClient,
      serverName,
      signal,
    );
    const url = new URL(
      `/_synapse/admin/v1/registration_tokens/${encodeURIComponent(tokenId)}`,
      synapseRoot,
    );
    const synapseBody: {
      uses_allowed?: number | null;
      expiry_time?: number | null;
    } = {};
    if (parameters.usage_limit !== undefined) {
      synapseBody.uses_allowed = parameters.usage_limit;
    }
    if (parameters.expires_at !== undefined) {
      synapseBody.expiry_time = parameters.expires_at
        ? Date.parse(parameters.expires_at)
        : null;
    }
    const response = await fetch(url, {
      method: "PUT",
      headers,
      body: JSON.stringify(synapseBody),
      signal,
    });
    ensureResponseOk(response);
    const record = (await response.json()) as SynapseRegistrationTokenRecord;
    const resource = mapSynapseTokenToResource(record);
    return {
      data: resource,
      links: resource.links,
    };
  }
  const body: api.UpdateUserRegistrationTokenData["body"] = {};

  if (parameters.expires_at !== undefined)
    body.expires_at = parameters.expires_at;
  if (parameters.usage_limit !== undefined)
    body.usage_limit = parameters.usage_limit;

  const result = await api.updateUserRegistrationToken({
    ...(await masBaseOptions(queryClient, serverName, signal)),
    path: { id: tokenId },
    body,
  });
  ensureNoError(result);
  return result.data;
};

const applyOauth2ClientFilters = (
  query: api.ListOAuth2ClientsData["query"] & {},
  parameters: OAuth2ClientListParameters,
): void => {
  if (parameters.kind) query["filter[client-kind]"] = parameters.kind;
  if (parameters.name) query["filter[client-name]"] = parameters.name;
  if (parameters.uri) query["filter[client-uri]"] = parameters.uri;
  if (parameters.grantType) query["filter[grant-type]"] = parameters.grantType;
  if (parameters.hasActiveSessions !== undefined)
    query["filter[has-active-sessions]"] = parameters.hasActiveSessions;
};

export const oauth2ClientsInfiniteQuery = (
  serverName: string,
  parameters: OAuth2ClientListParameters = {},
) =>
  infiniteQueryOptions({
    queryKey: ["mas", "oauth2-clients", serverName, parameters],
    queryFn: async ({
      client,
      signal,
      pageParam,
    }): Promise<WithData<api.PaginatedResponseForOAuth2Client>> => {
      const query: api.ListOAuth2ClientsData["query"] & {} = {
        "page[first]": PAGE_SIZE,
        count: "false",
      };

      if (pageParam) query["page[after]"] = pageParam;
      applyOauth2ClientFilters(query, parameters);

      const result = await api.listOAuth2Clients({
        ...(await masBaseOptions(client, serverName, signal)),
        query,
      });
      ensureNoError(result);
      ensureHasData(result.data);
      return result.data;
    },
    initialPageParam: null as api.Ulid | null,
    getNextPageParam: (lastPage): api.Ulid | null =>
      (lastPage.links.next && cursorForSingleResource(lastPage.data?.at(-1))) ??
      null,
  });

export const oauth2ClientsCountQuery = (
  serverName: string,
  parameters: OAuth2ClientListParameters = {},
) =>
  queryOptions({
    queryKey: ["mas", "oauth2-clients", serverName, parameters, "count"],
    queryFn: async ({ client, signal }): Promise<number> => {
      const query: api.ListOAuth2ClientsData["query"] & {} = {
        count: "only",
      };

      applyOauth2ClientFilters(query, parameters);

      const result = await api.listOAuth2Clients({
        ...(await masBaseOptions(client, serverName, signal)),
        query,
      });
      ensureNoError(result);
      ensureHasCount(result.data);
      return result.data.meta.count;
    },
  });

export const oauth2ClientQuery = (serverName: string, clientId: string) =>
  queryOptions({
    queryKey: ["mas", "oauth2-client", serverName, clientId],
    queryFn: async ({
      client,
      signal,
    }): Promise<api.SingleResponseForOAuth2Client> => {
      const result = await api.getOAuth2Client({
        ...(await masBaseOptions(client, serverName, signal)),
        path: { id: clientId },
      });
      ensureNoError(result, true);
      return result.data;
    },
  });

const applyOauth2SessionFilters = (
  query: api.ListOAuth2SessionsData["query"] & {},
  parameters: OAuth2SessionListParameters,
): void => {
  if (parameters.user) query["filter[user]"] = parameters.user;
  if (parameters.client && parameters.client.length > 0)
    query["filter[client]"] = parameters.client;
  if (parameters.clientKind)
    query["filter[client-kind]"] = parameters.clientKind;
  if (parameters.userSession)
    query["filter[user-session]"] = parameters.userSession;
  if (parameters.scope && parameters.scope.length > 0)
    query["filter[scope]"] = parameters.scope;
  if (parameters.status) query["filter[status]"] = parameters.status;
  if (parameters.createdAfter)
    query["filter[created-after]"] = parameters.createdAfter;
  if (parameters.createdBefore)
    query["filter[created-before]"] = parameters.createdBefore;
  if (parameters.lastActiveAfter)
    query["filter[last-active-after]"] = parameters.lastActiveAfter;
  if (parameters.lastActiveBefore)
    query["filter[last-active-before]"] = parameters.lastActiveBefore;
};

export const oauth2SessionsInfiniteQuery = (
  serverName: string,
  parameters: OAuth2SessionListParameters = {},
  direction: PaginationDirection = "forward",
) =>
  infiniteQueryOptions({
    queryKey: ["mas", "oauth2-sessions", serverName, parameters, direction],
    queryFn: async ({
      client,
      signal,
      pageParam,
    }): Promise<WithData<api.PaginatedResponseForOAuth2Session>> => {
      const query: api.ListOAuth2SessionsData["query"] & {} = {
        count: "false",
      };

      if (direction === "forward") {
        query["page[first]"] = PAGE_SIZE;
        if (pageParam) query["page[after]"] = pageParam;
      } else {
        query["page[last]"] = PAGE_SIZE;
        if (pageParam) query["page[before]"] = pageParam;
      }

      applyOauth2SessionFilters(query, parameters);

      const result = await api.listOAuth2Sessions({
        ...(await masBaseOptions(client, serverName, signal)),
        query,
      });
      ensureNoError(result);
      ensureHasData(result.data);
      return result.data;
    },
    initialPageParam: null as api.Ulid | null,
    getNextPageParam: (lastPage): api.Ulid | null =>
      (direction === "forward"
        ? lastPage.links.next && cursorForSingleResource(lastPage.data?.at(-1))
        : lastPage.links.prev &&
          cursorForSingleResource(lastPage.data?.at(0))) ?? null,
  });

export const oauth2SessionsCountQuery = (
  serverName: string,
  parameters: OAuth2SessionListParameters = {},
) =>
  queryOptions({
    queryKey: ["mas", "oauth2-sessions", serverName, parameters, "count"],
    queryFn: async ({ client, signal }): Promise<number> => {
      const query: api.ListOAuth2SessionsData["query"] & {} = {
        count: "only",
      };

      applyOauth2SessionFilters(query, parameters);

      const result = await api.listOAuth2Sessions({
        ...(await masBaseOptions(client, serverName, signal)),
        query,
      });
      ensureNoError(result);
      ensureHasCount(result.data);
      return result.data.meta.count;
    },
  });

export const oauth2SessionQuery = (serverName: string, sessionId: string) =>
  queryOptions({
    queryKey: ["mas", "oauth2-session", serverName, sessionId],
    queryFn: async ({
      client,
      signal,
    }): Promise<api.SingleResponseForOAuth2Session> => {
      const result = await api.getOAuth2Session({
        ...(await masBaseOptions(client, serverName, signal)),
        path: { id: sessionId },
      });
      ensureNoError(result, true);
      return result.data;
    },
  });

export const finishOauth2Session = async (
  queryClient: QueryClient,
  serverName: string,
  sessionId: api.Ulid,
  signal?: AbortSignal,
): Promise<api.SingleResponseForOAuth2Session> => {
  const result = await api.finishOAuth2Session({
    ...(await masBaseOptions(queryClient, serverName, signal)),
    path: { id: sessionId },
  });
  ensureNoError(result);
  return result.data;
};

const applyCompatSessionFilters = (
  query: api.ListCompatSessionsData["query"] & {},
  parameters: CompatSessionListParameters,
): void => {
  if (parameters.user) query["filter[user]"] = parameters.user;
  if (parameters.userSession)
    query["filter[user-session]"] = parameters.userSession;
  if (parameters.status) query["filter[status]"] = parameters.status;
  if (parameters.createdAfter)
    query["filter[created-after]"] = parameters.createdAfter;
  if (parameters.createdBefore)
    query["filter[created-before]"] = parameters.createdBefore;
  if (parameters.lastActiveAfter)
    query["filter[last-active-after]"] = parameters.lastActiveAfter;
  if (parameters.lastActiveBefore)
    query["filter[last-active-before]"] = parameters.lastActiveBefore;
};

export const compatSessionsInfiniteQuery = (
  serverName: string,
  parameters: CompatSessionListParameters = {},
  direction: PaginationDirection = "forward",
) =>
  infiniteQueryOptions({
    queryKey: ["mas", "compat-sessions", serverName, parameters, direction],
    queryFn: async ({
      client,
      signal,
      pageParam,
    }): Promise<WithData<api.PaginatedResponseForCompatSession>> => {
      const query: api.ListCompatSessionsData["query"] & {} = {
        count: "false",
      };

      if (direction === "forward") {
        query["page[first]"] = PAGE_SIZE;
        if (pageParam) query["page[after]"] = pageParam;
      } else {
        query["page[last]"] = PAGE_SIZE;
        if (pageParam) query["page[before]"] = pageParam;
      }

      applyCompatSessionFilters(query, parameters);

      const result = await api.listCompatSessions({
        ...(await masBaseOptions(client, serverName, signal)),
        query,
      });
      ensureNoError(result);
      ensureHasData(result.data);
      return result.data;
    },
    initialPageParam: null as api.Ulid | null,
    getNextPageParam: (lastPage): api.Ulid | null =>
      (direction === "forward"
        ? lastPage.links.next && cursorForSingleResource(lastPage.data?.at(-1))
        : lastPage.links.prev &&
          cursorForSingleResource(lastPage.data?.at(0))) ?? null,
  });

export const compatSessionsCountQuery = (
  serverName: string,
  parameters: CompatSessionListParameters = {},
) =>
  queryOptions({
    queryKey: ["mas", "compat-sessions", serverName, parameters, "count"],
    queryFn: async ({ client, signal }): Promise<number> => {
      const query: api.ListCompatSessionsData["query"] & {} = {
        count: "only",
      };

      applyCompatSessionFilters(query, parameters);

      const result = await api.listCompatSessions({
        ...(await masBaseOptions(client, serverName, signal)),
        query,
      });
      ensureNoError(result);
      ensureHasCount(result.data);
      return result.data.meta.count;
    },
  });

export const compatSessionQuery = (serverName: string, sessionId: string) =>
  queryOptions({
    queryKey: ["mas", "compat-session", serverName, sessionId],
    queryFn: async ({
      client,
      signal,
    }): Promise<api.SingleResponseForCompatSession> => {
      const result = await api.getCompatSession({
        ...(await masBaseOptions(client, serverName, signal)),
        path: { id: sessionId },
      });
      ensureNoError(result, true);
      return result.data;
    },
  });

export const finishCompatSession = async (
  queryClient: QueryClient,
  serverName: string,
  sessionId: api.Ulid,
  signal?: AbortSignal,
): Promise<api.SingleResponseForCompatSession> => {
  const result = await api.finishCompatSession({
    ...(await masBaseOptions(queryClient, serverName, signal)),
    path: { id: sessionId },
  });
  ensureNoError(result);
  return result.data;
};
